import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { getSavedMicrosoftProviderNames } from '../DataProvider/DataProvider.utils';
import SOURCE_TYPE from '../SourceCreate.constants';
import { SourceDialogMode } from '../SourceCreate.types';
import type {
  UseDataProviderValidationParams,
  UseDataProviderValidationReturn
} from './SourceCreateDialog.types';
import {
  isAwsS3ProviderSelected,
  isGoogleStorageProviderSelected,
  isMicrosoftProviderSelected
} from './SourceCreateDialog.utils';

/**
 * Custom hook to manage data provider selection validation.
 *
 * Business rule: AWS S3, Google Storage, and Microsoft sources require
 * at least one data provider to be selected. This validation is enforced
 * by setting a hidden 'data_provider_selection' field.
 *
 * @param params - Watch function, setValue, source type info
 * @returns Boolean indicating if any provider is selected
 */
export const useDataProviderValidation = ({
  setValue,
  clearErrors,
  control,
  selectedSourceTypeId,
  mode,
  selectedSourceDetails
}: UseDataProviderValidationParams): UseDataProviderValidationReturn => {
  const dataProvider = useWatch({ control, name: 'data_provider' });
  const selectionFlag = useWatch({
    control,
    name: 'data_provider_selection'
  });

  /**
   * Checks if any data provider is selected for multi-provider sources.
   * Delegates to utility functions for cleaner, testable logic.
   */
  const checkIfAnyProviderSelected = (): boolean => {
    if (selectedSourceTypeId === SOURCE_TYPE.AWS_S3) {
      return isAwsS3ProviderSelected(dataProvider);
    }

    if (selectedSourceTypeId === SOURCE_TYPE.GOOGLE_STORAGE) {
      return isGoogleStorageProviderSelected(dataProvider);
    }

    if (selectedSourceTypeId === SOURCE_TYPE.MICROSOFT) {
      const savedProviders =
        mode === SourceDialogMode.Edit &&
        selectedSourceDetails?.ms_data_providers
          ? getSavedMicrosoftProviderNames(selectedSourceDetails)
          : [];

      return isMicrosoftProviderSelected(dataProvider, savedProviders);
    }

    return false;
  };

  const isAnySelected = useMemo(
    () => checkIfAnyProviderSelected(),
    [dataProvider, selectedSourceTypeId, mode, selectedSourceDetails]
  );

  // Update hidden validation field when provider selection changes.
  // Clear stale errors explicitly so the error disappears as soon as a provider is selected.
  useEffect(() => {
    const selectionValue = isAnySelected ? 'YES' : '';

    if (isAnySelected) {
      clearErrors('data_provider_selection');
    }

    if (selectionFlag !== selectionValue) {
      setValue('data_provider_selection', selectionValue, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [isAnySelected, selectionFlag, setValue, clearErrors]);

  return { isAnySelected };
};
